import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './Payment.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CheckoutForm({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required'
    });

    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess();
    } else {
      setError('Payment could not be completed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="payment-amount-display">
        <span>Amount to pay:</span>
        <strong>£{amount.toFixed(2)}</strong>
      </div>
      <PaymentElement onReady={() => setReady(true)} />
      {error && <div className="payment-error">{error}</div>}
      <div className="payment-actions">
        <button type="submit" disabled={!stripe || !ready || processing} className="pay-btn">
          {processing ? 'Processing...' : `Pay £${amount.toFixed(2)}`}
        </button>
        <button type="button" onClick={onCancel} className="cancel-pay-btn" disabled={processing}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Payment({ apiEndpoint, idToken, vehicleId, balance, entries, onClose, onPaymentComplete }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [step, setStep] = useState('select'); // select | checkout | success
  const [error, setError] = useState(null);

  const paymentOptions = [
    ...(balance > 0 ? [{ label: `Full balance`, amount: balance }] : []),
    ...entries.slice(0, 5).map(e => ({
      label: `Flight ${e.date} (${e.tachoStart}–${e.tachoFinish})`,
      amount: e.entryCost
    }))
  ];

  const handleSelectAmount = async (amount, description) => {
    setError(null);
    try {
      const res = await fetch(`${apiEndpoint}/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: idToken },
        body: JSON.stringify({ amount, vehicleId, description })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to initialise payment');
      setSelectedAmount(amount);
      setClientSecret(data.clientSecret);
      setStep('checkout');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    const amt = parseFloat(customAmount);
    if (!amt || amt <= 0) { setError('Enter a valid amount'); return; }
    handleSelectAmount(amt, `Custom payment for ${vehicleId}`);
  };

  const handleSuccess = () => {
    setStep('success');
    setTimeout(() => {
      onPaymentComplete();
      onClose();
    }, 3000);
  };

  return (
    <div className="payment-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={e => e.stopPropagation()}>
        <button className="payment-close" onClick={onClose}>×</button>
        <h2>Make a Payment</h2>
        <p className="payment-vehicle">Aircraft: {vehicleId}</p>

        {step === 'select' && (
          <div className="payment-select">
            {error && <div className="payment-error">{error}</div>}
            <h3>Select amount</h3>
            <div className="payment-options">
              {paymentOptions.map((opt, i) => (
                <button
                  key={i}
                  className="payment-option-btn"
                  onClick={() => handleSelectAmount(opt.amount, opt.label)}
                >
                  <span className="option-label">{opt.label}</span>
                  <span className="option-amount">£{opt.amount.toFixed(2)}</span>
                </button>
              ))}
            </div>
            <div className="custom-amount-section">
              <h3>Or enter a custom amount</h3>
              <form onSubmit={handleCustomSubmit} className="custom-amount-form">
                <div className="amount-input-wrapper">
                  <span className="currency-symbol">£</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.50"
                    placeholder="0.00"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    className="custom-amount-input"
                  />
                </div>
                <button type="submit" className="pay-btn">Continue</button>
              </form>
            </div>
          </div>
        )}

        {step === 'checkout' && clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <CheckoutForm
              amount={selectedAmount}
              onSuccess={handleSuccess}
              onCancel={() => setStep('select')}
            />
          </Elements>
        )}

        {step === 'success' && (
          <div className="payment-success">
            <div className="success-icon">✓</div>
            <h3>Payment Successful!</h3>
            <p>£{selectedAmount.toFixed(2)} has been processed.</p>
            <p>A confirmation email has been sent to you.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Payment;
